import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hook/useAuth";
import { post_UserLogin } from "../services/Login";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { baseURL } from "../config/api";
import { getAccessControl } from "../services/AccessControl";
import { getAllowDenyStatus } from "../services/AllowStatus";
import useAccessControl from "../hook/useAccessControl";
import { appAdminRoutes } from "../routes/adminRoutes";
import { appAdminSettingRoutes } from "../routes/adminSettingsRoutes";

const Login = () => {
    const navigate = useNavigate();
    const { setAuth }: any = useAuth();
    const { setUserRoleId, setPermissions }: any = useAccessControl();
    const [passwordTextType, setPasswordTextType] = useState<boolean>(false);
    const [isRememberMe, setIsRememberMe] = useState<boolean>(true);
    const [userData, setUserData] = useState<any>({ userName: "", password: "" });
    const [fieldReadOnly, setFieldReadOnly] = useState<boolean>(false);
    const [errorMessage, setErroMessage] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isProcessing) {
            setIsProcessing(true);
            setFieldReadOnly(true);
            post_UserLogin({
                userName: userData.userName,
                password: userData.password,
            })
                .then((res) => {
                    getUserData(res);
                })
                .catch((error) => {
                    setIsProcessing(false);
                    setFieldReadOnly(false);
                    setErroMessage(error.response.data);
                    console.error(error);
                });
        }
    };

    const getUserData = (accessToken: string) => {
        const jsonData: any = jwtDecode(accessToken);
        const id =
            jsonData[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
            ];
        const roles =
            jsonData[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ];
        const givenname =
            jsonData[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
            ];
        const emailAddress =
            jsonData[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
            ];

        const userName = userData.userName;

        const expirationDate = Math.floor(Date.now() / 1000) + 3600;

        if (isRememberMe) {
            Cookies.set("token-express-tire", accessToken, {
                expires: jsonData.exp,
            });
            Cookies.set("role-express-tire", roles, {
                expires: jsonData.exp,
            });
            Cookies.set("userId-express-tire", id, {
                expires: jsonData.exp,
            });
            Cookies.set("userName-express-tire", userData.userName, {
                expires: jsonData.exp,
            });
            Cookies.set("fullName-express-tire", givenname, {
                expires: jsonData.exp,
            });
            Cookies.set("expires-express-tire", jsonData.exp, {
                expires: jsonData.exp,
            });
        } else {
            Cookies.set("token-express-tire", accessToken, {
                expires: new Date().setHours(new Date().getHours() + 1),
            });
            Cookies.set("role-express-tire", roles, {
                expires: new Date().setHours(new Date().getHours() + 1),
            });
            Cookies.set("userId-express-tire", id, {
                expires: new Date().setHours(new Date().getHours() + 1),
            });
            Cookies.set("userName-express-tire", userData.userName, {
                expires: new Date().setHours(new Date().getHours() + 1),
            });
            Cookies.set("fullName-express-tire", givenname, {
                expires: new Date().setHours(new Date().getHours() + 1),
            });
            Cookies.set("expires-express-tire", expirationDate?.toString(), {
                expires: expirationDate
            });
        }

        setAuth({
            id,
            userName,
            givenname,
            emailAddress,
            roles,
            accessToken,
        });
        setUserData({ userName: "", password: "" });
        getUserProfile(id, isRememberMe ? jsonData.exp : expirationDate, roles, accessToken);
    }

    const fetchPermissions = async (role: string, roleId: number, token: string) => {
        if (role.toLowerCase() === "admin") {
            navigate("/admin/dashboard", { replace: true });
            return;
        }

        try {
            const res: any = await getAccessControl("accessControls", { roleId }, token);
            const accessControlData = res;

            const response: any = await getAllowDenyStatus();
            const allowStatusId = response.find((item: any) => item.allowDenyName === "Allow")?.id;

            const newPermissions: any = {};

            accessControlData.forEach((item: any) => {
                const normalized = item.section.replace(/\s+/g, "").replace(/-/g, "").toLowerCase();
                newPermissions[normalized] = item.allowDenyStatusId === allowStatusId;
            });

            setPermissions(newPermissions);

            handleNavigationForAppRoutes(newPermissions);

        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    };

    const getUserProfile = (id: string, jsonData: any, roles: any, token: string) => {
        fetch(`${baseURL}users/${parseInt(id)}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Failed to fetch user profile");
                }
                return res.json();
            })
            .then((userData) => {
                const roleId = userData?.roleId;
                const profileImg = userData?.profilePic;

                setUserRoleId(roleId);

                Cookies.set("roleId-express-tire", roleId, {
                    expires: jsonData.exp,
                });

                Cookies.set("profileImg-express-tire", profileImg, {
                    expires: jsonData.exp,
                });

                fetchPermissions(roles, roleId, token);

                setIsProcessing(false);
                setFieldReadOnly(false);
            })
            .catch((error) => {
                setIsProcessing(false);
                console.error("Error fetching user profile:", error);
            });
    };


    const handleNavigationForAppRoutes = (permissions: any) => {
        const dashboardRoute = {
            path: "/admin/dashboard",
            section: "dashboard"
        };

        const hasDashboardPermission = permissions?.[dashboardRoute.section] || false;

        if (hasDashboardPermission) {
            navigate("/admin/dashboard", { replace: true });
            return;
        }

        const nextAvailableRoute = appAdminRoutes.find((route: any) => {
            return permissions?.[route.section] || false;
        });

        if (nextAvailableRoute) {
            navigate(nextAvailableRoute.link ?? "/unauthorized", { replace: true });
            return;
        }

        const nextAdminRoute = appAdminSettingRoutes?.find((route: any) => {
            return permissions?.[route.section] || false;
        });

        if (nextAdminRoute) {
            navigate(nextAdminRoute.link ?? "/unauthorized", { replace: true });
            return;
        }

        navigate("/unauthorized", { replace: true });
    };


    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] via-[#eef2f6] to-[#dce3eb] flex items-center justify-center p-4">

            <div className="w-full max-w-md -mt-28 bg-white/60 backdrop-blur-xl shadow-2xl border border-white/40 rounded-3xl p-10 animate-fadeIn">

                <div className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold bg-gradient-to-r from-[#121212] to-[#5b5b5b] bg-clip-text text-transparent">
                        Express Tire Works
                    </h1>
                    <p className="text-gray-600 mt-2 tracking-wide">Welcome back! Please sign in.</p>
                </div>

                <form className="space-y-6" onSubmit={handleLogin}>
                    <div className="group">
                        <label className="text-gray-800 font-semibold block mb-1">Username</label>
                        <input
                            type="text"
                            required
                            value={userData.userName}
                            onChange={(e) => setUserData({ ...userData, userName: e.target.value })}
                            readOnly={fieldReadOnly}
                            placeholder="Enter your username"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 shadow-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-gray-400"
                        />
                    </div>

                    <div>
                        <label className="text-gray-800 font-semibold block mb-1">Password</label>
                        <div className="relative">
                            <input
                                type={passwordTextType ? "text" : "password"}
                                required
                                value={userData.password}
                                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                                readOnly={fieldReadOnly}
                                placeholder="Enter your password"
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 shadow-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-gray-400"
                            />

                            <span
                                className="absolute right-4 top-3.5 cursor-pointer opacity-70 hover:opacity-100 transition"
                                onClick={() => setPasswordTextType(!passwordTextType)}
                            >
                                {passwordTextType ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="black" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M3 3l18 18M10.73 10.73A3 3 0 0113 12m3.95 3.95A8.06 8.06 0 0112 20c-5 0-9-4-10-8a14.38 14.38 0 014.24-5.94" />
                                        <path d="M9.88 5.54A8.06 8.06 0 0112 4c5 0 9 4 10 8a14.3 14.3 0 01-2.12 3.95" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="black" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                        <label className="flex items-center gap-2 text-gray-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isRememberMe}
                                onChange={() => setIsRememberMe(!isRememberMe)}
                                className="h-4 w-4 rounded border-gray-400"
                            />
                            <span className="text-sm">Remember me</span>
                        </label>

                        <Link className="text-sm font-semibold text-primary hover:text-black hover:underline" to="/forgot-password">
                            Forgot Password?
                        </Link>
                    </div>

                    <button
                        type="submit"
                        className="w-full py-3 bg-[#121212] text-white rounded-full text-sm font-semibold tracking-wide hover:bg-black transition-all shadow-lg"
                    >
                        {isProcessing ? (
                            <div className="flex justify-center items-center gap-2">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Loading...
                            </div>
                        ) : (
                            "Sign In"
                        )}
                    </button>

                    {errorMessage && (
                        <p className="text-center text-red-500 mt-4 text-sm">{errorMessage}</p>
                    )}
                </form>
            </div>
        </div>

    );
};

export default Login;